/**
 * Embedded API client
 * Proxies API requests through parent application
 */

import type { IApiClient } from './types'
import type { Conversation, Message } from '../types/chat'

export interface EmbeddedApiConfig {
  baseUrl: string
  request: <T>(endpoint: string, options?: RequestInit) => Promise<T>
}

export class EmbeddedApiClient implements IApiClient {
  private config: EmbeddedApiConfig

  constructor(config: EmbeddedApiConfig) {
    this.config = config
  }

  getBaseUrl(): string {
    return this.config.baseUrl
  }

  async fetchConversations(params?: {
    channel?: string
    fanpageName?: string
    search?: string
  }): Promise<Conversation[]> {
    const queryParams = new URLSearchParams()
    if (params?.channel) queryParams.set('channel', params.channel)
    if (params?.fanpageName) queryParams.set('fanpageName', params.fanpageName)
    if (params?.search) queryParams.set('search', params.search)

    return this.request<Conversation[]>(
      `/conversations?${queryParams.toString()}`
    )
  }

  async fetchMessages(
    conversationId: string,
    params?: { limit?: number; offset?: number; before?: string }
  ): Promise<Message[]> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.set('limit', params.limit.toString())
    if (params?.offset) queryParams.set('offset', params.offset.toString())
    if (params?.before) queryParams.set('before', params.before)

    return this.request<Message[]>(
      `/conversations/${conversationId}/messages?${queryParams.toString()}`
    )
  }

  async sendMessage(
    conversationId: string,
    content: string,
    attachments?: File[]
  ): Promise<Message> {
    const formData = new FormData()
    formData.append('content', content)

    if (attachments) {
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file)
      })
    }

    return this.request<Message>(
      `/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: formData
      }
    )
  }

  async markAsRead(conversationId: string, messageIds: string[]): Promise<void> {
    await this.request<void>(
      `/conversations/${conversationId}/read`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageIds })
      }
    )
  }

  async uploadFile(file: File): Promise<{ url: string; id: string }> {
    const formData = new FormData()
    formData.append('file', file)

    return this.request<{ url: string; id: string }>(
      '/upload',
      {
        method: 'POST',
        body: formData
      }
    )
  }

  async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    // Delegate to parent's request handler
    return this.config.request<T>(endpoint, options)
  }
}
